import axios from '@crema/services/axios';
import qs from 'qs';
import { deleteIndexedDB, getIndexedDB, saveIndexedDB } from '@libs/helpers/src';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getLocalStorage, LOCAL_STORAGE_NAMES, removeLocalStorage, setLocalStorage } from '@libs/storage/src/lib/local';
import { SESSION_STORAGE_NAMES, setSessionStorage } from '@libs/storage/src/lib/session';

type ErrorDataType = {
  detail: string;
  errorKey: string;
  status: number;
  title: string;
};
const jwtAxios = axios.create({
  baseURL: process.env.NX_API_URL,
  headers: {
    'X-Language': getLocalStorage(LOCAL_STORAGE_NAMES.LANG) || 'KO',
  },
  paramsSerializer: {
    serialize: (params: Record<string, string | boolean | number>) => {
      return qs.stringify(params, { encode: true });
    },
    index: null,
  },
});

let tokenRefreshPromise: Promise<string> | undefined;

export const refreshAccessToken = async (): Promise<string> => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = new Promise(async (resolve, reject) => {
      try {
        const refreshToken = await getIndexedDB('refresh-token');
        //jwtAxios 사용할경우 만료된 access-token이 포함되어 401 에러가 발생하기 때문에 기본 axios 사용
        const response = await axios.post(`${process.env.NX_API_URL}oauth/refresh`, {
          refreshToken: refreshToken,
        });

        const { access_token, refresh_token } = response.data;

        setJwtAccessToken(access_token);
        await saveIndexedDB('refresh-token', refresh_token);

        resolve(access_token);
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          // 인증 실패
          // console.log('accessToken not vaild');

          // 로그아웃 처리 및 현재 경로를 쿼리로 전달
          let signinUrl = '/signin';
          try {
            const search = window.location.search || '';
            const pathname = window.location.pathname || '';
            const current = `${pathname}${search}`;
            signinUrl = `/signin?redirect-url=${encodeURIComponent(current)}`;
          } catch (e) {}
          setJwtAccessToken();
          await deleteIndexedDB('refresh-token');
          setLocalStorage(LOCAL_STORAGE_NAMES.ERROR, 'error.expiredToken'); // 로그인 페이지에서 오류 출력 위해 저장
          window.location.replace(signinUrl);
        }
        reject(error);
      } finally {
        // 토큰 갱신이 완료되면 Promise 초기화
        tokenRefreshPromise = undefined;
      }
    });
  }

  return await tokenRefreshPromise;
};

jwtAxios.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    // 토큰 가져오기
    const accessToken = getLocalStorage(LOCAL_STORAGE_NAMES.TOKEN);

    config.headers = config.headers || {};
    // 헤더에 accessToken 추가
    if (config && accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      // config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  async (error: any) => {
    return Promise.reject(error);
  },
);

jwtAxios.interceptors.response.use(
  (res: AxiosResponse<any, any>) => res,
  async (err: AxiosError<ErrorDataType>) => {
    if (err.response) {
      // 인증 실패
      const { errorKey } = err.response.data;

      if (errorKey === 'passwordExpired') {
        // 이미 비밀번호 만료 페이지에 있으면 리다이렉트 하지 않음 (무한 리다이렉트 방지)
        if (!window.location.pathname.startsWith('/password-expired')) {
          const token = getLocalStorage(LOCAL_STORAGE_NAMES.TOKEN);
          sessionStorage.setItem('passwordExpiredToken', token || '');
          window.location.href = `/password-expired?token=${token}`;
        }

        return Promise.reject(err);
      }

      if (err.response.status === 401) {
        if (errorKey && (errorKey === 'tokenHasExpired' || errorKey === 'tokenDoesNotExist')) {
          // accessToken 만료
          // console.log('accessToken Expried!!');
          const originalRequest = err.config;
          const newAccessToken = await refreshAccessToken();
          // refreshToken을 사용하여 새로운 accessToken을 요청합니다.
          if (originalRequest) {
            if (newAccessToken) {
              // console.log('newAccessToken  = ', newAccessToken);
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              // 실패한 api 재시도
              return jwtAxios(originalRequest);
            }
          }
        }
      }

      // store.dispatch({type: LOGOUT});
    }
    return Promise.reject(err);
  },
);

export const setJwtAccessToken = (token?: string) => {
  if (token) {
    jwtAxios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setLocalStorage(LOCAL_STORAGE_NAMES.TOKEN, token);
  } else {
    delete jwtAxios.defaults.headers.common.Authorization;
    removeLocalStorage(LOCAL_STORAGE_NAMES.TOKEN);
  }
};

export const setJwtRefreshToken = (token?: string) => {
  if (token) {
    setLocalStorage(LOCAL_STORAGE_NAMES.REFRESH_TOKEN, token);
  } else {
    removeLocalStorage(LOCAL_STORAGE_NAMES.REFRESH_TOKEN);
  }
};

export const setUserInfo = (user: any) => {
  if (user) {
    setSessionStorage(SESSION_STORAGE_NAMES.CHANNEL, JSON.stringify(user.channel));
  }
};

export const setLanguage = (language: string | undefined) => {
  jwtAxios.defaults.headers.common['Accept-Language'] = language;
  if (language) {
    setLocalStorage(LOCAL_STORAGE_NAMES.LANG, language.toUpperCase());
  }
};

export default jwtAxios;
